// feature-module/people/Suppliers.jsx
import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import Select from 'react-select';
import {
    Plus, Search, RotateCcw,
    Eye, Edit, Trash2, ChevronUp,
    User, Mail, Phone,
} from 'feather-icons-react/build/IconComponents';
import { supplierActions } from "../../core/action/supplierAction";
import { userActions } from '../../core/action/userAction';
import SupplierModal from "../../core/modals/peoples/supplierModal";

const MySwal = withReactContent(Swal);

const Suppliers = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);

    // Get user role for super admin check
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.role_slug === 'super-admin';

    // Permission checks
    const canCreate = userActions.hasPermission('create-suppliers');
    const canEdit = userActions.hasPermission('edit-suppliers');
    const canDelete = userActions.hasPermission('delete-suppliers');
    const canView = userActions.hasPermission('view-suppliers');

    // State management
    const [suppliers, setSuppliers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);

    // Modal states
    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState("add");
    const [selectedSupplier, setSelectedSupplier] = useState(null);

    // Pagination
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 1
    });

    // Filters
    const [filters, setFilters] = useState({
        search: '',
        country: '',
        status: '',
        sortBy: 'name',
        sortOrder: 'ASC'
    });

    // Sort options
    const sortOptions = [
        { value: 'name_asc', label: 'Name (A-Z)' },
        { value: 'name_desc', label: 'Name (Z-A)' },
        { value: 'email_asc', label: 'Email (A-Z)' },
        { value: 'email_desc', label: 'Email (Z-A)' },
        { value: 'created_desc', label: 'Newest First' },
        { value: 'created_asc', label: 'Oldest First' }
    ];

    const [sortOption, setSortOption] = useState('created_desc');

    // Statistics
    const [stats, setStats] = useState({
        total: 0,
        active: 0,
        inactive: 0
    });

    // Load suppliers from API
    const loadSuppliers = useCallback(async (page = pagination.page) => {
        // Check if user has view permission
        if (!canView && !isSuperAdmin) {
            console.warn('User does not have permission to view suppliers');
            return;
        }

        try {
            setLoading(true);

            const [sortBy, sortOrder] = sortOption.split('_');

            const params = {
                page,
                limit: pagination.limit,
                search: filters.search,
                country: filters.country,
                status: filters.status,
                sort_by: sortBy === 'created' ? 'created_at' : sortBy,
                sort_order: sortOrder === 'desc' ? 'DESC' : 'ASC'
            };

            const result = await supplierActions.getSuppliers(params);

            if (result.success) {
                setSuppliers(result.data || []);
                setPagination({
                    page: result.page || page,
                    limit: pagination.limit,
                    total: result.total || 0,
                    totalPages: result.totalPages || 1
                });
            } else {
                toast.error(result.message || 'Failed to load suppliers');
            }
        } catch (error) {
            console.error('Error loading suppliers:', error);
            toast.error('Failed to load suppliers');
        } finally {
            setLoading(false);
        }
    }, [canView, isSuperAdmin, pagination.page, pagination.limit, filters.search, filters.country, filters.status, sortOption]);

    // Handle add supplier
    const handleAddSupplier = () => {
        if (!canCreate && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to create suppliers',
                icon: 'error',
                timer: 3000
            });
            return;
        }
        setModalMode("add");
        setSelectedSupplier(null);
        setShowModal(true);
    };

    // Handle edit supplier
    const handleEditSupplier = (supplier) => {
        if (!canEdit && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to edit suppliers',
                icon: 'error',
                timer: 3000
            });
            return;
        }
        setModalMode("edit");
        setSelectedSupplier(supplier);
        setShowModal(true);
    };

    // Handle view supplier
    const handleViewSupplier = (supplier) => {
        if (!canView && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to view supplier details',
                icon: 'error',
                timer: 3000
            });
            return;
        }
        setModalMode("view");
        setSelectedSupplier(supplier);
        setShowModal(true);
    };

    // Handle delete supplier
    const handleDeleteSupplier = async (id, name) => {
        if (!canDelete && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to delete suppliers',
                icon: 'error',
                timer: 3000
            });
            return;
        }

        const result = await MySwal.fire({
            title: 'Delete Supplier?',
            html: `<p>Are you sure you want to delete <strong>${name}</strong>?</p>
                   <p class="text-danger">This action cannot be undone.</p>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Yes, delete it!'
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const deleteResult = await supplierActions.deleteSupplier(id);

                if (deleteResult.success) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: 'Supplier has been deleted.',
                        timer: 2000
                    });
                    loadSuppliers(pagination.page);
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: deleteResult.message || 'Failed to delete supplier',
                        timer: 2000
                    });
                }
            } catch (error) {
                console.error('Error deleting supplier:', error);
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to delete supplier',
                    timer: 2000
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle bulk delete
    const handleBulkDelete = async () => {
        if (!canDelete && !isSuperAdmin) {
            MySwal.fire({
                title: 'Access Denied',
                text: 'You do not have permission to delete suppliers',
                icon: 'error',
                timer: 3000
            });
            return;
        }

        if (selectedRows.length === 0) {
            MySwal.fire({
                icon: 'info',
                title: 'No Selection',
                text: 'Please select at least one supplier to delete.',
                timer: 2000
            });
            return;
        }

        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: `You want to delete ${selectedRows.length} supplier(s)?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: `Yes, delete ${selectedRows.length} item(s)`
        });

        if (result.isConfirmed) {
            try {
                setLoading(true);
                const deletePromises = selectedRows.map(id => supplierActions.deleteSupplier(id));
                const results = await Promise.allSettled(deletePromises);
                const successful = results.filter(r => r.status === 'fulfilled' && r.value?.success);

                if (successful.length > 0) {
                    MySwal.fire({
                        icon: 'success',
                        title: 'Deleted!',
                        text: `${successful.length} supplier(s) deleted.`,
                        timer: 2000
                    });
                    setSelectedRows([]);
                    loadSuppliers(pagination.page);
                } else {
                    MySwal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Failed to delete suppliers',
                        timer: 2000
                    });
                }
            } catch (error) {
                console.error('Error in bulk delete:', error);
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: 'Failed to delete suppliers',
                    timer: 2000
                });
            } finally {
                setLoading(false);
            }
        }
    };

    // Handle modal close
    const handleModalClose = (refresh = false) => {
        setShowModal(false);
        setSelectedSupplier(null);
        if (refresh) {
            loadSuppliers(pagination.page);
        }
    };

    // Reset filters
    const resetFilters = () => {
        setFilters({
            search: '',
            country: '',
            status: '',
            sortBy: 'name',
            sortOrder: 'ASC'
        });
        setSortOption('created_desc');
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    // Update statistics when suppliers change
    useEffect(() => {
        const active = suppliers.filter(s => s.status === 'active').length;
        const inactive = suppliers.filter(s => s.status === 'inactive').length;

        setStats({
            total: suppliers.length,
            active,
            inactive
        });
    }, [suppliers]);

    // Initial load
    useEffect(() => {
        if (canView || isSuperAdmin) {
            loadSuppliers();
        }
    }, []); // Empty dependency array

    // Update when sort, page, or limit changes
    useEffect(() => {
        if (canView || isSuperAdmin) {
            loadSuppliers();
        }
    }, [sortOption, pagination.page, pagination.limit]);

    // Debounced search - reload when the search text changes (including when cleared)
    const isFirstSearchRender = useRef(true);
    useEffect(() => {
        if (isFirstSearchRender.current) {
            isFirstSearchRender.current = false;
            return;
        }
        if (!canView && !isSuperAdmin) return;
        const timer = setTimeout(() => {
            loadSuppliers(1);
        }, 400);
        return () => clearTimeout(timer);
    }, [filters.search]);

    // Get unique countries for filter
    const countries = [...new Set(suppliers.map(s => s.country).filter(Boolean))];

    // Get supplier initials
    const getInitials = (name) => {
        return name
            .split(' ')
            .map(word => word[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    // Get random color for avatar
    const getAvatarColor = (id) => {
        const colors = [
            'bg-primary',
            'bg-success',
            'bg-info',
            'bg-warning',
            'bg-danger',
            'bg-secondary'
        ];
        return colors[(id % colors.length)];
    };

    // Tooltip renderer
    const renderTooltip = (props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    );

    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="page-title">
                        <h4>Supplier Management</h4>
                        <h6>Manage your suppliers and their information</h6>
                    </div>

                    <ul className="table-top-head">

                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <Link onClick={() => loadSuppliers()}>
                                    <RotateCcw />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <Link
                                    className={data ? "active" : ""}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp />
                                </Link>
                            </OverlayTrigger>
                        </li>
                        {/* Bulk Delete Button - Only show if user has delete permission */}
                        {(canDelete || isSuperAdmin) && selectedRows.length > 0 && (
                            <li>
                                <button className="btn btn-danger btn-sm" onClick={handleBulkDelete}>
                                    <Trash2 size={16} className="me-1" />
                                    Delete ({selectedRows.length})
                                </button>
                            </li>
                        )}
                    </ul>

                    {/* Add Supplier Button - Only show if user has create permission */}
                    <div className="page-btn">
                        {(canCreate || isSuperAdmin) && (
                            <button className="btn btn-primary" onClick={handleAddSupplier}>
                                <Plus size={18} className="me-2" />
                                Add Supplier
                            </button>
                        )}
                    </div>
                </div>

                {/* Statistics Cards - Only show if user has view permission */}
                {(canView || isSuperAdmin) && (
                    <div className="row">
                        <div className="col-lg-4 col-md-6 col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 p-3 rounded me-3">
                                            <User size={24} className="text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-muted mb-1">Total Suppliers</p>
                                            <h3 className="mb-0">{stats.total}</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-success bg-opacity-10 p-3 rounded me-3">
                                            <User size={24} className="text-success" />
                                        </div>
                                        <div>
                                            <p className="text-muted mb-1">Active</p>
                                            <h3 className="mb-0">{stats.active}</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="col-lg-4 col-md-6 col-sm-12">
                            <div className="card">
                                <div className="card-body">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-danger bg-opacity-10 p-3 rounded me-3">
                                            <User size={24} className="text-danger" />
                                        </div>
                                        <div>
                                            <p className="text-muted mb-1">Inactive</p>
                                            <h3 className="mb-0">{stats.inactive}</h3>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Filters - Only show if user has view permission */}
                {(canView || isSuperAdmin) && (
                    <div className="card mb-4">
                        <div className="card-body">
                            <div className="row">
                                <div className="col-lg-3">
                                    <div className="mb-3">
                                        <label>Search</label>
                                        <div className="input-group">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder="Name, email, phone..."
                                                value={filters.search}
                                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                                onKeyPress={(e) => e.key === 'Enter' && loadSuppliers(1)}
                                            />
                                            <button className="btn btn-primary" onClick={() => loadSuppliers(1)}>
                                                <Search size={16} />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                <div className="col-lg-2">
                                    <div className="mb-3">
                                        <label>Country</label>
                                        <select
                                            className="form-select"
                                            value={filters.country}
                                            onChange={(e) => {
                                                setFilters({ ...filters, country: e.target.value });
                                                setPagination(prev => ({ ...prev, page: 1 }));
                                            }}
                                        >
                                            <option value="">All Countries</option>
                                            {countries.map(country => (
                                                <option key={country} value={country}>{country}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-2">
                                    <div className="mb-3">
                                        <label>Status</label>
                                        <select
                                            className="form-select"
                                            value={filters.status}
                                            onChange={(e) => {
                                                setFilters({ ...filters, status: e.target.value });
                                                setPagination(prev => ({ ...prev, page: 1 }));
                                            }}
                                        >
                                            <option value="">All Status</option>
                                            <option value="active">Active</option>
                                            <option value="inactive">Inactive</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="col-lg-3">
                                    <div className="mb-3">
                                        <label>Sort By</label>
                                        <Select
                                            options={sortOptions}
                                            value={sortOptions.find(s => s.value === sortOption)}
                                            onChange={(opt) => {
                                                setSortOption(opt?.value || 'created_desc');
                                                setPagination(prev => ({ ...prev, page: 1 }));
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className="col-lg-2 d-flex align-items-end">
                                    <button className="btn btn-secondary w-100" onClick={resetFilters}>
                                        Reset
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Suppliers Table */}
                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : !canView && !isSuperAdmin ? (
                            <div className="text-center py-5">
                                <i data-feather="lock" className="feather-lock" style={{ fontSize: '48px', opacity: 0.5 }} />
                                <h5 className="mt-3">Access Denied</h5>
                                <p className="text-muted">You dont have permission to view suppliers</p>
                            </div>
                        ) : suppliers.length === 0 ? (
                            <div className="text-center py-5">
                                <User size={48} className="text-muted mb-3" />
                                <h5>No suppliers found</h5>
                                <p className="text-muted">
                                    {filters.search || filters.country || filters.status
                                        ? "Try adjusting your search or filters"
                                        : "Add your first supplier to get started"}
                                </p>
                                {(canCreate || isSuperAdmin) && (
                                    <button className="btn btn-primary" onClick={handleAddSupplier}>
                                        <Plus size={16} className="me-2" />
                                        Add Supplier
                                    </button>
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="table-top">
                                    <div className="search-set">
                                        <div className="search-input">
                                            <input
                                                type="text"
                                                placeholder="Quick search..."
                                                className="form-control"
                                                value={filters.search}
                                                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                                                onKeyPress={(e) => e.key === 'Enter' && loadSuppliers(1)}
                                            />
                                            <button className="btn btn-searchset">
                                                <Search size={16} />
                                            </button>
                                        </div>
                                    </div>
                                    <div className="wordset">
                                        <span className="badge bg-primary">{suppliers.length} entries</span>
                                    </div>
                                </div>

                                <div className="table-responsive">
                                    <table className="table table-hover">
                                        <thead>
                                            <tr>
                                                {/* Checkbox column - Only show if user has delete permission */}
                                                {(canDelete || isSuperAdmin) && (
                                                    <th width="40px">
                                                        <input
                                                            type="checkbox"
                                                            className="form-check-input"
                                                            checked={selectedRows.length === suppliers.length}
                                                            onChange={(e) => {
                                                                if (e.target.checked) {
                                                                    setSelectedRows(suppliers.map(s => s.id));
                                                                } else {
                                                                    setSelectedRows([]);
                                                                }
                                                            }}
                                                        />
                                                    </th>
                                                )}
                                                <th>Code</th>
                                                <th>Supplier Name</th>
                                                <th>Contact Info</th>
                                                <th>Location</th>
                                                <th>Status</th>
                                                <th className="text-center">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {suppliers.map((supplier) => (
                                                <tr key={supplier.id}>
                                                    {/* Checkbox cell - Only show if user has delete permission */}
                                                    {(canDelete || isSuperAdmin) && (
                                                        <td>
                                                            <input
                                                                type="checkbox"
                                                                className="form-check-input"
                                                                checked={selectedRows.includes(supplier.id)}
                                                                onChange={(e) => {
                                                                    if (e.target.checked) {
                                                                        setSelectedRows([...selectedRows, supplier.id]);
                                                                    } else {
                                                                        setSelectedRows(selectedRows.filter(id => id !== supplier.id));
                                                                    }
                                                                }}
                                                            />
                                                        </td>
                                                    )}
                                                    <td>
                                                        <span className="badge bg-light text-dark">
                                                            {supplier.code || 'N/A'}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            <div className={`avatar avatar-sm me-2 ${getAvatarColor(supplier.id)} rounded-circle d-flex align-items-center justify-content-center text-white`}>
                                                                {getInitials(supplier.name)}
                                                            </div>
                                                            <div>
                                                                <strong>{supplier.name}</strong>
                                                                {supplier.contact_person && (
                                                                    <div className="small text-muted">
                                                                        Contact: {supplier.contact_person}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            {supplier.email && (
                                                                <div className="small">
                                                                    <Mail size={12} className="me-1 text-muted" />
                                                                    {supplier.email}
                                                                </div>
                                                            )}
                                                            {supplier.phone && (
                                                                <div className="small">
                                                                    <Phone size={12} className="me-1 text-muted" />
                                                                    {supplier.phone}
                                                                </div>
                                                            )}
                                                            {!supplier.email && !supplier.phone && (
                                                                <span className="text-muted">No contact</span>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {supplier.city || supplier.country ? (
                                                            <div className="d-flex flex-column">
                                                                {supplier.city && (
                                                                    <span className="small">{supplier.city}</span>
                                                                )}
                                                                {supplier.country && (
                                                                    <span className="small text-muted">{supplier.country}</span>
                                                                )}
                                                            </div>
                                                        ) : (
                                                            <span className="text-muted">N/A</span>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <span className={`badge ${supplier.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                                            {supplier.status || 'inactive'}
                                                        </span>
                                                    </td>
                                                    <td className="text-center">
                                                        {/* View Button - Show if user has view permission */}
                                                        {(canView || isSuperAdmin) && (
                                                            <button
                                                                className="btn btn-sm btn-outline-info me-1"
                                                                onClick={() => handleViewSupplier(supplier)}
                                                                title="View Details"
                                                            >
                                                                <Eye size={14} />
                                                            </button>
                                                        )}

                                                        {/* Edit Button - Show if user has edit permission */}
                                                        {(canEdit || isSuperAdmin) && (
                                                            <button
                                                                className="btn btn-sm btn-outline-primary me-1"
                                                                onClick={() => handleEditSupplier(supplier)}
                                                                title="Edit"
                                                            >
                                                                <Edit size={14} />
                                                            </button>
                                                        )}

                                                        {/* Delete Button - Show if user has delete permission */}
                                                        {(canDelete || isSuperAdmin) && (
                                                            <button
                                                                className="btn btn-sm btn-outline-danger"
                                                                onClick={() => handleDeleteSupplier(supplier.id, supplier.name)}
                                                                title="Delete"
                                                            >
                                                                <Trash2 size={14} />
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Pagination */}
                                {pagination.total > 0 && (
                                    <div className="d-flex justify-content-between align-items-center mt-3">
                                        <div className="text-muted">
                                            Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
                                            {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                        </div>
                                        {pagination.totalPages > 1 && (
                                            <nav>
                                                <ul className="pagination">
                                                    <li className={`page-item ${pagination.page === 1 ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => loadSuppliers(pagination.page - 1)}>
                                                            Previous
                                                        </button>
                                                    </li>
                                                    {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                                                        let pageNum = pagination.page;
                                                        if (pagination.totalPages <= 5) {
                                                            pageNum = i + 1;
                                                        } else if (pagination.page <= 3) {
                                                            pageNum = i + 1;
                                                        } else if (pagination.page >= pagination.totalPages - 2) {
                                                            pageNum = pagination.totalPages - 4 + i;
                                                        } else {
                                                            pageNum = pagination.page - 2 + i;
                                                        }
                                                        return (
                                                            <li key={pageNum} className={`page-item ${pagination.page === pageNum ? 'active' : ''}`}>
                                                                <button className="page-link" onClick={() => loadSuppliers(pageNum)}>
                                                                    {pageNum}
                                                                </button>
                                                            </li>
                                                        );
                                                    })}
                                                    <li className={`page-item ${pagination.page === pagination.totalPages ? 'disabled' : ''}`}>
                                                        <button className="page-link" onClick={() => loadSuppliers(pagination.page + 1)}>
                                                            Next
                                                        </button>
                                                    </li>
                                                </ul>
                                            </nav>
                                        )}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>

            {showModal && (
                <SupplierModal
                    mode={modalMode}
                    supplier={selectedSupplier}
                    onClose={handleModalClose}
                />
            )}
        </div>
    );
};

export default Suppliers;